import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { refreshSession, setAccessToken, setSessionLostHandler } from '../api/client'
import type { SessionResponse, SignedInUser } from '../api/contracts'

import { authApi } from './auth-api'
import { AuthContext, type AuthState } from './auth-context'

interface AuthProviderProps {
  readonly children: ReactNode
}

/**
 * Kimin girdiğini tutar ve erişim token'ını depolamanın dışında bırakır
 * (`docs/standards/frontend-standards.md §7`).
 *
 * Token'ın kendisi bu state'te değil, api istemcisinin modül kapsamında yaşar: her sessiz
 * yenilemede değişir ve arka plandaki bir istek onu her tazelediğinde tüm ağacı yeniden çizmek
 * hem israf hem şaşırtıcı olurdu. Burada duran şey kimliktir; o da yalnızca biri girip çıktığında
 * değişir.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<SignedInUser | null>(null)
  const [isRestoring, setIsRestoring] = useState(true)

  const signIn = useCallback((session: SessionResponse) => {
    setAccessToken(session.accessToken)
    setUser(session.user)
  }, [])

  const clear = useCallback(() => {
    setAccessToken(null)
    setUser(null)
  }, [])

  const signOut = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // Yutuluyor, yeniden fırlatılmıyor. Çıkış, kullanıcının yüzünde başarısız olmaması gereken
      // tek aksiyondur: kişi ayrılmak istedi; hâlâ girmiş göründüğü bir ekranın üstündeki hata
      // penceresi hem ürkütücü hem faydasızdır. Çağrı başarısız olduysa cookie sunucuda bir süre
      // daha yaşayabilir, ama bu cihazdaki oturum her hâlükârda biter.
    } finally {
      clear()
    }
  }, [clear])

  useEffect(() => {
    // Api istemcisi, yenileme kalıcı olarak başarısız olduğunda bunu çağırır; bu, açılıştan çok
    // sonra bir istek ortasında da olabilir. Ağacın bunu tek yerden öğrenmesi için burada kayıtlı.
    setSessionLostHandler(clear)

    return () => setSessionLostHandler(null)
  }, [clear])

  useEffect(() => {
    let cancelled = false

    /**
     * Sayfa yüklenirken oturumu geri yükler. Erişim token'ı gitmiştir — zaten yalnız bellekte
     * yaşıyordu — ama refresh cookie'si durur; böylece yenileme penceresi içindeki bir sayfa
     * tazelemesi oturumu bitirmez, sürdürür.
     *
     * Yenileme **api istemcisinin** üzerinden yapılıyor, buradan ayrı bir `fetch` ile değil:
     * rotasyonlu token'da ikinci bir yenileme yolu, ilkinin tüketilmiş token'ını sunup sunucuya
     * aileyi iptal ettirir. Burası bir zamanlar kendi çağrısını yapıyordu ve sayfa yenilemesinin
     * sekizde biri kullanıcıyı giriş ekranına atıyordu (`client.ts §refreshSession`).
     */
    async function restore(): Promise<void> {
      try {
        const session = await refreshSession()

        // Token'ı `refreshSession` zaten yazdı; burada tutulan şey kimlik (standards §7).
        if (!cancelled && session !== null) {
          setUser(session.user)
        }
      } finally {
        if (!cancelled) {
          setIsRestoring(false)
        }
      }
    }

    void restore()

    return () => {
      cancelled = true
    }
  }, [])

  /**
   * Oturumu yerinde tazeler: aynı kişi, yeni jeton.
   *
   * İki adımlı doğrulamayı zorunlu kurulumdan geçerek açan kullanıcı için var (KARAR 2026-08-20).
   * Elindeki jeton hâlâ "kurulum borçlu" diyor ve tazelenmeden, kişi az önce istenen şeyi
   * yaptığı hâlde her kapıda reddedilmeye devam ederdi — jetonun kendi ömrü dolana kadar.
   */
  const refresh = useCallback(async () => {
    const session = await refreshSession()

    if (session !== null) {
      setUser(session.user)
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({ user, isRestoring, signIn, signOut, refresh }),
    [user, isRestoring, signIn, signOut, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
